import { ApiError } from "@/lib/api";

/**
 * Map a backend ApiError to an i18n key suffix for a UI message.
 *
 * The backend returns specific `detail` strings for the anti-pattern guards
 * documented in ADR-0010 (e.g. "Cannot demote the last admin"). We surface
 * those directly via lookup so the UI stays in sync with the backend
 * wording without round-tripping through error codes.
 */
type ErrorBucket = "self" | "lastAdmin" | "duplicate" | "hasDependents" | "demoteSelf" | "deactivateSelf" | "generic";

const DETAIL_MAP: Array<[RegExp, ErrorBucket]> = [
  [/cannot demote themselves/i, "demoteSelf"],
  [/cannot deactivate themselves/i, "deactivateSelf"],
  [/cannot delete themselves/i, "self"],
  [/cannot demote the last admin/i, "lastAdmin"],
  [/cannot deactivate the last admin/i, "lastAdmin"],
  [/cannot delete the last admin/i, "lastAdmin"],
  [/already exists/i, "duplicate"],
  [/dependent records/i, "hasDependents"],
];

export function classifyApiError(err: unknown): ErrorBucket {
  if (!(err instanceof ApiError)) return "generic";
  const detail = err.detail;
  const text =
    typeof detail === "string"
      ? detail
      : typeof detail === "object" &&
          detail !== null &&
          "detail" in detail &&
          typeof (detail as { detail: unknown }).detail === "string"
        ? ((detail as { detail: string }).detail)
        : err.message;
  for (const [re, bucket] of DETAIL_MAP) {
    if (re.test(text)) return bucket;
  }
  return "generic";
}
